import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  SupportSessionState,
  getSupportSession,
  setSupportSession,
  subscribeSupportSession,
} from "@/lib/supportSession";
import {
  installSupportReadOnlyGuard,
  removeSupportReadOnlyGuard,
} from "@/lib/supportReadOnlyGuard";
import {
  installSupportWriteAudit,
  removeSupportWriteAudit,
} from "@/lib/supportWriteAudit";
import { queryClient } from "@/lib/queryClient";
import { jobsCache } from "@/lib/offlineDb";
import { isNative } from "@/lib/capacitor";

interface SupportSessionContextValue {
  session: SupportSessionState | null;
  isSupportSession: boolean;
  isReadOnly: boolean;
  startSession: (args: { companyId: string; companyName: string; mode: "read_only" | "editing" }) => Promise<boolean>;
  enableEditing: () => Promise<void>;
  endSession: (options?: { reload?: boolean }) => Promise<void>;
  logEvent: (event: { type: "page_view" | "action" | "mode_change"; path?: string; description?: string; metadata?: Record<string, unknown> }) => void;
}

const SupportSessionContext = createContext<SupportSessionContextValue | null>(null);

async function bestEffortIp(): Promise<string | null> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2000);
    const res = await fetch("https://api.ipify.org?format=json", { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    const json = await res.json();
    return typeof json?.ip === "string" ? json.ip : null;
  } catch {
    return null;
  }
}

export function SupportSessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SupportSessionState | null>(() => getSupportSession());

  // Keep React state in sync with the module store (single source of truth)
  useEffect(() => subscribeSupportSession(setSession), []);

  // Backend is authoritative: if this tab's session was ended elsewhere or
  // expired (4h inactivity), stop using it. Checked on load and tab focus only.
  useEffect(() => {
    if (!session?.id) return;
    let cancelled = false;
    const check = async () => {
      const active = getSupportSession();
      if (!active) return;
      const { data, error } = await (supabase.rpc as any)("is_support_session_valid", { _session_id: active.id });
      if (cancelled || error || data !== false) return;
      await (supabase.rpc as any)("expire_stale_support_sessions").then(() => undefined, () => undefined);
      setSupportSession(null);
      queryClient.clear();
      if (isNative) await jobsCache.clear().catch(() => undefined);
      removeSupportReadOnlyGuard();
      removeSupportWriteAudit();
      window.location.replace("/sa/support-sessions");
    };
    void check();
    const onVis = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [session?.id]);

  // Install / remove the read-only guard (read only) or the write audit
  // (editing) exactly when needed. Never both — they wrap the same client.
  useEffect(() => {
    if (session && session.mode === "read_only") {
      removeSupportWriteAudit();
      installSupportReadOnlyGuard();
    } else if (session && session.mode === "editing") {
      removeSupportReadOnlyGuard();
      installSupportWriteAudit();
    } else {
      removeSupportReadOnlyGuard();
      removeSupportWriteAudit();
    }
  }, [session]);

  const logEvent = useCallback<SupportSessionContextValue["logEvent"]>((event) => {
    const active = getSupportSession();
    if (!active) return;
    void (supabase.from("support_session_events") as any)
      .insert({
        session_id: active.id,
        event_type: event.type,
        path: event.path ?? null,
        description: event.description ?? null,
        metadata: event.metadata ?? {},
      })
      .then(() => undefined, () => undefined);
  }, []);

  const startSession = useCallback<SupportSessionContextValue["startSession"]>(
    async ({ companyId, companyName, mode }) => {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const user = authSession?.user;
      if (!user) return false;

      const ip = await bestEffortIp();

      const { data, error } = await (supabase.from("support_sessions") as any)
        .insert({
          super_admin_id: user.id,
          super_admin_email: user.email ?? null,
          company_id: companyId,
          company_name: companyName,
          mode,
          editing_enabled_at: mode === "editing" ? new Date().toISOString() : null,
          ip_address: ip,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        })
        .select("id, started_at")
        .single();

      if (error || !data) {
        console.error("[SupportSession] Failed to start session", error);
        return false;
      }

      setSupportSession({
        id: data.id,
        companyId,
        companyName,
        mode,
        startedAt: data.started_at ?? new Date().toISOString(),
      });
      // This is a tenant trust-boundary transition. Never retain data fetched
      // under the platform identity or a previous customer context.
      queryClient.clear();
      if (isNative) await jobsCache.clear().catch(() => undefined);
      return true;
    },
    []
  );

  const enableEditing = useCallback(async () => {
    const active = getSupportSession();
    if (!active || active.mode === "editing") return;
    const next: SupportSessionState = { ...active, mode: "editing" };
    setSupportSession(next);
    await (supabase.from("support_sessions") as any)
      .update({ mode: "editing", editing_enabled_at: new Date().toISOString() })
      .eq("id", active.id);
    logEvent({ type: "mode_change", description: "Editing enabled" });
  }, [logEvent]);

  const endSession = useCallback(async (options?: { reload?: boolean }) => {
    const active = getSupportSession();
    if (!active) return;

    const endedAt = new Date();
    const duration = Math.max(
      0,
      Math.round((endedAt.getTime() - new Date(active.startedAt).getTime()) / 1000)
    );

    try {
      // Only close an open row; if expiry already closed it, zero rows is fine.
      await (supabase.from("support_sessions") as any)
        .update({ ended_at: endedAt.toISOString(), duration_seconds: duration, ended_reason: "manual" })
        .eq("id", active.id)
        .is("ended_at", null);
    } catch (e) {
      console.error("[SupportSession] Failed to close session", e);
    }

    setSupportSession(null);
    queryClient.clear();
    if (isNative) await jobsCache.clear().catch(() => undefined);
    removeSupportReadOnlyGuard();
    removeSupportWriteAudit();

    if (options?.reload !== false) {
      // Hard reload clears every cached customer object and refreshes permissions
      window.location.replace("/sa/support-sessions");
    }
  }, []);

  const value = useMemo<SupportSessionContextValue>(
    () => ({
      session,
      isSupportSession: !!session,
      isReadOnly: !!session && session.mode === "read_only",
      startSession,
      enableEditing,
      endSession,
      logEvent,
    }),
    [session, startSession, enableEditing, endSession, logEvent]
  );

  return (
    <SupportSessionContext.Provider value={value}>
      <div key={session?.companyId ?? "platform"} className="contents">{children}</div>
    </SupportSessionContext.Provider>
  );
}

export function useSupportSession(): SupportSessionContextValue {
  const ctx = useContext(SupportSessionContext);
  if (!ctx) {
    // Safe no-op fallback so the hook can be used anywhere without breaking existing trees
    return {
      session: null,
      isSupportSession: false,
      isReadOnly: false,
      startSession: async () => false,
      enableEditing: async () => undefined,
      endSession: async () => undefined,
      logEvent: () => undefined,
    };
  }
  return ctx;
}

/** Convenience hook for disabling mutating UI while in read-only support mode. */
export function useSupportReadOnly(): boolean {
  return useSupportSession().isReadOnly;
}
